import * as Sentry from '@sentry/node';
import { Injectable } from '@gitlab-org/di';
import { ConfigService } from '../common';
import { getLanguageServerVersion } from '../common/utils/get_language_server_version';
import { ErrorTracker } from '../common/tracking/error_tracker';
import { SanitizedError } from '../common/errors/sanitized_error';

@Injectable(ErrorTracker, [ConfigService])
export class NodeSentryTracker {
  #configService: ConfigService;

  constructor(configService: ConfigService) {
    this.#configService = configService;

    Sentry.init({
      dsn: 'https://e91efe8a2479ca2e503712398b6503ee@new-sentry.gitlab.net/34',
      tracesSampleRate: 1.0,
      environment: process.env.NODE_ENV,
      release: getLanguageServerVersion(),
      beforeSend: (event) => {
        return this.#removePrivateInfoFields(event);
      },
      integrations: [Sentry.rewriteFramesIntegration()],
      // no breadcrumbs until we have advanced scrubbing in place to prevent private information from leaking
      maxBreadcrumbs: 0,
    });
  }

  isEnabled(): boolean {
    const enabledSetting = this.#configService.get('client.telemetry.enabled');
    return enabledSetting ?? false;
  }

  trackError(e: SanitizedError): void {
    if (!this.isEnabled()) return;
    const telemetrySetting = this.#configService.get('client.telemetry');
    Sentry.setContext('client', {
      ide: telemetrySetting?.ide,
      extension: telemetrySetting?.extension,
    });
    Sentry.captureException({ message: e.message });
  }

  #removePrivateInfoFields(event: Sentry.ErrorEvent) {
    const updatedEvent = event;

    if (updatedEvent.server_name) {
      delete updatedEvent.server_name;
    }
    if (updatedEvent.contexts?.culture) {
      delete updatedEvent.contexts.culture;
    }
    if (updatedEvent.contexts?.device) {
      delete updatedEvent.contexts?.device;
    }
    if (updatedEvent.contexts?.app) {
      delete updatedEvent.contexts?.app;
    }
    return updatedEvent;
  }
}
